import type React from "react";
import { useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import clsx from "clsx";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { signInUser } from "../features/auth/authSlice";
import { forgotPassword } from "../features/auth/authAPI";
import bgImage from "../assets/images/bg-img2.png";

type FormErrors = {
  email?: string;
  password?: string;
};

export default function LoginPage() {
  // const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loginStatus, loginError, loginFieldErrors } =
    useAppSelector((state) => state.auth);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordError, setForgotPasswordError] = useState<string | null>(null);
  const [isSendingResetEmail, setIsSendingResetEmail] = useState(false);

  const isSubmitting = loginStatus === "loading";
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
  const baseInputClasses =
    "w-full bg-[#F7FAFC] px-4 py-3 rounded-md focus:outline-none placeholder:text-[#4A5568]/60 transition-colors";
  const inputClasses = (hasError: boolean) =>
    clsx(
      baseInputClasses,
      hasError  
        ? "border border-red-400 focus:ring-2 focus:ring-red-400 focus:border-red-400"
        : "border border-gray-200 focus:ring-2 focus:ring-lime-400 focus:border-transparent",
    );

  const validateForm = (): FormErrors => {
    const errors: FormErrors = {};
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      errors.email = "Email is required.";
    } else if (!emailRegex.test(trimmedEmail)) {
      errors.email = "Enter a valid email address.";
    }

    if (!password) {
      errors.password = "Password is required.";
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    setSubmissionError(null);

    try {
      await dispatch(
        signInUser({
          email: email.trim(),
          password,
          rememberMe,
        }),
      ).unwrap();
    } catch (error) {
      setSubmissionError(
        typeof error === "string"
          ? error
          : "Unable to sign in. Please try again.",
      );
    }
  };

  useEffect(() => {
    if (!loginFieldErrors) {
      return;
    }

    const mappedErrors: FormErrors = {};
    if (loginFieldErrors.email?.length) {
      mappedErrors.email = loginFieldErrors.email[0];
    }
    if (loginFieldErrors.password?.length || loginFieldErrors.password1?.length) {
      mappedErrors.password =
        loginFieldErrors.password?.[0] ?? loginFieldErrors.password1?.[0];
    }

    setFormErrors((prev) => ({ ...prev, ...mappedErrors }));

    const firstError =
      loginFieldErrors.email?.[0] ??
      loginFieldErrors.password?.[0] ??
      loginFieldErrors.password1?.[0];

    if (firstError) {
      setSubmissionError(firstError);
    } else if (loginError) {
      setSubmissionError(loginError);
    }
  }, [loginFieldErrors, loginError]);

  useEffect(() => {
    if (loginStatus === "succeeded") {
      toast.success("Signed in successfully.");
    }
  }, [loginStatus]);

  useEffect(() => {
    const errorMessage = submissionError ?? loginError;
    if (errorMessage) {
      toast.error(errorMessage);
    }
  }, [submissionError, loginError]);

  const openForgotPasswordModal = () => {
    setForgotPasswordEmail(email.trim());
    setForgotPasswordError(null);
    setIsForgotPasswordOpen(true);
  };

  const closeForgotPasswordModal = () => {
    if (isSendingResetEmail) return;
    setIsForgotPasswordOpen(false);
    setForgotPasswordError(null);
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedEmail = forgotPasswordEmail.trim();
    if (!trimmedEmail) {
      setForgotPasswordError("Email is required.");
      return;
    }
    if (!emailRegex.test(trimmedEmail)) {
      setForgotPasswordError("Enter a valid email address.");
      return;
    }

    setForgotPasswordError(null);
    setIsSendingResetEmail(true);

    try {
      const response = await forgotPassword({ email: trimmedEmail });
      toast.success(response.message || "Email has been sent.");
      setIsForgotPasswordOpen(false);
      setForgotPasswordEmail("");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Unable to send password reset email. Please try again.";
      setForgotPasswordError(message);
      toast.error(message);
    } finally {
      setIsSendingResetEmail(false);
    }
  };

  // const handleGoogleLogin = () => {
  //   console.log("Google login clicked");
  // };

  // const handleFacebookLogin = () => {
  //   console.log("Facebook login clicked");
  // };

  return (
    <div className="min-h-screen flex">
      {isForgotPasswordOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <form
            onSubmit={handleForgotPasswordSubmit}
            className="w-full max-w-md rounded-md bg-white p-6 shadow-xl"
          >
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-[#171923]">Forgot password?</h2>
                <p className="mt-2 text-sm text-[#718096]">
                  Enter your email address and we'll send you password reset instructions.
                </p>
              </div>
              <button
                type="button"
                onClick={closeForgotPasswordModal}
                className="text-2xl leading-none text-gray-400 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isSendingResetEmail}
                aria-label="Close forgot password popup"
              >
                ×
              </button>
            </div>

            <label className="block text-base font-medium text-[#718096] mb-2">
              E-mail
            </label>
            <input
              type="email"
              placeholder="example@gmail.com"
              value={forgotPasswordEmail}
              onChange={(e) => setForgotPasswordEmail(e.target.value)}
              className={inputClasses(Boolean(forgotPasswordError))}
              aria-invalid={Boolean(forgotPasswordError)}
              aria-describedby={
                forgotPasswordError ? "forgot-password-email-error" : undefined
              }
              disabled={isSendingResetEmail}
              autoFocus
              required
            />
            {forgotPasswordError && (
              <p id="forgot-password-email-error" className="mt-1 text-sm text-red-600">
                {forgotPasswordError}
              </p>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeForgotPasswordModal}
                className="rounded-md px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isSendingResetEmail}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-cta rounded-md px-4 py-2 text-sm font-medium disabled:opacity-70"
                disabled={isSendingResetEmail}
              >
                {isSendingResetEmail ? "Sending..." : "Continue"}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 md:px-12 lg:px-16 py-12 bg-white">
        <div className="max-w-lg mx-auto contents h-full w-full">
          {/* Header */}
          <h1 className="text-5xl font-bold text-[#171923] mb-3">Get Started Now</h1>
          <p className="flex text-[#171923] text-lg mb-12">
            Enter your credentials to access your account
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="md:space-y-8 space-y-10">
            {submissionError && (
              <div
                className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                role="alert"
                aria-live="assertive"
              >
                {submissionError}
              </div>
            )}

            {/* Email Input */}
            <div>
              <label className="block text-base font-medium text-[#718096] mb-2">
                E-mail
              </label>
              <input
                type="email"
                placeholder="example@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClasses(Boolean(formErrors.email))}
                aria-invalid={Boolean(formErrors.email)}
                aria-describedby={formErrors.email ? "login-email-error" : undefined}
                required
              />
              {formErrors.email && (
                <p id="login-email-error" className="mt-1 text-sm text-red-600">
                  {formErrors.email}
                </p>
              )}
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-base font-medium text-[#718096] mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClasses(Boolean(formErrors.password))}
                  aria-invalid={Boolean(formErrors.password)}
                  aria-describedby={
                    formErrors.password ? "login-password-error" : undefined
                  }
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 px-1 h-2/3 top-1/2 border-l-2 border-[#CFD9E0] -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <Eye className="w-5 h-5" />
                  ) : (
                  <EyeOff className="w-5 h-5" />
                  )}
                </button>
              </div>
              {formErrors.password && (
                <p id="login-password-error" className="mt-1 text-sm text-red-600">
                  {formErrors.password}
                </p>
              )}
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-end text-sm">
              {/* <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 border border-gray-300 rounded-md cursor-pointer accent-lime-400"
                />
                <span className="text-[#718096] font-normal">Remember me</span>
              </label> */}
              {/* <p
                onClick={() => {
                  navigate("/forgot-password");
                }}
                className="text-[#1C4532] font-medium hover:underline cursor-pointer"
              >
                Forgot Password?
              </p> */}
              <button
                type="button"
                onClick={openForgotPasswordModal}
                className="text-[#1C4532] font-medium hover:underline"
              >
                Forgot Password?
              </button>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              className="btn-cta w-full py-3 font-medium rounded-md mt-2 disabled:opacity-70"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>
      </div>

      {/* Right Section - Branding */}
      <div
        className="hidden lg:flex w-1/2 h-screen bg-cover bg-center items-center justify-center p-4"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <div className=" rounded-md flex items-center justify-center">
          <img
            src="/assets/images/Logo-white-outline.png"
            alt="Join Us"
            className="w-48 h-auto object-contain"
          />
        </div>
      </div>
    </div>
  );
}
